import { Button } from "@dropbox/dig-components/buttons";
import { Text, Title } from "@dropbox/dig-components/typography";

import { Box } from "@dropbox/dig-foundations";
import { TargetMissSpot } from "@dropbox/dig-illustrations";
import { useTranslation } from "react-i18next";

import { tagged } from "@mirage/service-logging";
import { ONE_HOUR_IN_MILLIS } from "@mirage/shared/util/constants";
import * as Sentry from "@sentry/react";
import styles from "./AppErrorFallback.module.css";

import React from "react";

const logger = tagged("AppErrorFallback");

const RELOAD_KEY = "lastForcedReload";

const AppErrorFallback: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Box className={styles.errorBody}>
      <Box className={styles.icon}>
        <TargetMissSpot />
      </Box>
      <Title>{t("something_went_wrong")}</Title>
      <Text style={{ textAlign: "center" }}>
        {t("something_went_wrong_description")}
      </Text>
      <Box className={styles.buttons}>
        <Button variant="primary" onClick={() => window.location.reload()}>
          {t("refresh_page")}
        </Button>
      </Box>
    </Box>
  );
};

/**
 * Handle chunk load error.
 * If the user has forced a reload in the last hour, do nothing.
 * Otherwise, reload the page to try and refetch index.html and the updated web app.
 *
 * This is to prevent infinite reload loops. The user can still force a reload
 * by clicking the refresh button.
 */
const handleChunkLoadError = () => {
  const now = Date.now();
  try {
    const lastReload = parseInt(localStorage.getItem(RELOAD_KEY) || "0", 10);

    if (now - lastReload >= ONE_HOUR_IN_MILLIS) {
      localStorage.setItem(RELOAD_KEY, now.toString());
      logger.info("[AppContentErrorBound] Reloading due to ChunkLoadError");
      window.location.reload();
    } else {
      logger.info(
        `[AppContentErrorBound] Suppressed reload due to recent ChunkLoadError (last reload ${Math.round(
          (now - lastReload) / 60000,
        )} min ago)`,
      );
    }
  } catch (e) {
    logger.warn("[AppContentErrorBound] Failed to access localStorage:", e);
    // Fallback: reload anyway if storage fails
    window.location.reload();
  }
};

export const appErrorHandler = (error: Error) => {
  Sentry.captureMessage(
    `[AppContentErrorBound] Error thrown from AppContent: ${error.message}`,
    {
      tags: {
        original_message: error.message,
        error_name: error.name,
        error_stack: error.stack || "No stack trace",
      },
    },
  );
  logger.error(
    `[AppContentErrorBound] Error thrown from AppContent: ${error.name}: ${error.message}`,
  );
  if (error.name === "ChunkLoadError") {
    handleChunkLoadError();
  }
};

export default AppErrorFallback;
